/**
 * Filet de sécurité sur les attributs de ligne du panier.
 *
 * Ce script relit le panier, récupère les attributs attendus depuis les options
 * de chaque ligne et réécrit ceux qui manquent, pour que le checkout et la
 * commande les portent toujours, quelle que soit l'origine de la ligne.
 */
(() => {
  const script = document.currentScript;
  const SPLIT_NAME_SEP = (script && script.dataset.splitNameSep) || '//';
  const SPLIT_VALUE_SEP = (script && script.dataset.splitValueSep) || '/';

  const routes = (window.theme && window.theme.routes) || {};
  const CART_URL = `${routes.cart || '/cart'}.js`;
  const CHANGE_URL = routes.cartChange || '/cart/change.js';

  let repairing = false;

  const expectedProperties = (item) => {
    const expected = {};
    const options = item.options_with_values || [];

    if (options.length === 1 && options[0].name === 'Title' && options[0].value === 'Default Title') {
      return expected;
    }

    options.forEach((option) => {
      const name = String(option.name || '');
      const value = String(option.value || '');

      if (name.includes(SPLIT_NAME_SEP)) {
        const names = name.split(SPLIT_NAME_SEP);
        const values = value.split(SPLIT_VALUE_SEP);
        names.forEach((namePart, index) => {
          const key = namePart.trim();
          const val = (values[index] || '').trim();
          if (key && val) expected[key] = val;
        });
      } else {
        const key = name.trim();
        const val = value.trim();
        if (key && val) expected[key] = val;
      }
    });

    return expected;
  };

  const missingProperties = (item) => {
    const expected = expectedProperties(item);
    const current = item.properties || {};
    const merged = { ...current };
    let incomplete = false;

    Object.entries(expected).forEach(([key, value]) => {
      if (!current[key]) {
        merged[key] = value;
        incomplete = true;
      }
    });

    return incomplete ? merged : null;
  };

  const repairOnce = async () => {
    const response = await fetch(CART_URL);
    if (!response.ok) return false;

    const cart = await response.json();
    if (!cart.items || cart.items.length === 0) return false;

    for (let i = 0; i < cart.items.length; i += 1) {
      const item = cart.items[i];
      const properties = missingProperties(item);

      if (properties) {
        const result = await fetch(CHANGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties })
        });
        return result.ok;
      }
    }

    return false;
  };

  const refreshCartUi = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      cartDrawer.refresh();
      return;
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') cartItems.refresh();
  };

  const repairCart = async () => {
    if (repairing) return;
    repairing = true;

    let repaired = false;
    try {
      for (let pass = 0; pass < 50; pass += 1) {
        // eslint-disable-next-line no-await-in-loop
        const didRepair = await repairOnce();
        if (!didRepair) break;
        repaired = true;
      }
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      repairing = false;
    }

    if (repaired) refreshCartUi();
  };

  let scheduled = null;

  const scheduleRepair = () => {
    clearTimeout(scheduled);
    scheduled = setTimeout(repairCart, 500);
  };

  const isCartMutation = (url) => /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(String(url));

  const watchCartMutations = () => {
    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function fetchWithCartRepair(...args) {
        const url = args[0] instanceof Request ? args[0].url : args[0];
        const promise = nativeFetch.apply(this, args);

        if (!repairing && isCartMutation(url)) {
          promise.then((response) => {
            if (response.ok) scheduleRepair();
            return response;
          }).catch(() => { });
        }

        return promise;
      };
    }

    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function openWithCartRepair(method, url, ...rest) {
      if (isCartMutation(url)) {
        this.addEventListener('load', () => {
          if (!repairing && this.status >= 200 && this.status < 300) scheduleRepair();
        });
      }
      return nativeOpen.call(this, method, url, ...rest);
    };
  };

  const handleCheckout = (evt) => {
    const trigger = evt.target.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (!trigger || trigger.dataset.cartRepairDone) return;

    evt.preventDefault();
    trigger.dataset.cartRepairDone = 'true';

    clearTimeout(scheduled);
    const proceed = () => trigger.click();
    Promise.race([repairCart(), new Promise((resolve) => { setTimeout(resolve, 1500); })])
      .then(proceed, proceed);
  };

  watchCartMutations();
  document.addEventListener('click', handleCheckout, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repairCart);
  } else {
    repairCart();
  }
})();
